import { useMemo } from 'react';

/**
 * Hook to optimize modal settings based on the current environment
 * Detects whether we're in Safari or other browsers
 */
export const useModalOptimization = () => {
  return useMemo(() => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    
    // Determine the environment
    const isStandardSafari = isIOS && isSafari;
    const isOtherBrowser = !isIOS || !isSafari;
    
    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Calculate optimal modal settings based on environment
    const getModalSettings = () => {
      if (isStandardSafari) {
        // Standard Safari - account for browser UI and address bar
        return {
          position: 'center',
          maxHeight: '75vh', // More conservative for Safari's dynamic viewport
          maxWidth: '90vw',
          padding: 'p-4',
          spacing: 'space-y-3',
          textSize: 'text-sm',
          iconSize: 'w-8 h-8',
          buttonSize: 'px-4 py-2',
          className: 'safari-optimized'
        };
      } else {
        // Other browsers (Chrome, Firefox, etc.)
        return {
          position: 'center',
          maxHeight: '80vh',
          maxWidth: '90vw',
          padding: 'p-4',
          spacing: 'space-y-3',
          textSize: 'text-sm',
          iconSize: 'w-8 h-8',
          buttonSize: 'px-4 py-2',
          className: 'browser-optimized'
        };
      }
    };
    
    const modalSettings = getModalSettings();
    
    return {
      // Environment detection
      isStandardSafari,
      isOtherBrowser,
      
      // Viewport info
      viewportHeight,
      viewportWidth,
      
      // Modal settings
      modalSettings,
      
      // Helper functions
      getOptimalModalProps: (customProps = {}) => ({
        position: modalSettings.position,
        maxWidth: modalSettings.maxWidth,
        className: `${modalSettings.className} max-h-[${modalSettings.maxHeight}] overflow-y-auto`,
        ...customProps
      }),
      
      getOptimalContentProps: (customProps = {}) => ({
        className: `${modalSettings.padding} ${modalSettings.spacing}`,
        ...customProps
      }),
      
      getOptimalTextProps: (customProps = {}) => ({
        className: modalSettings.textSize,
        ...customProps
      }),
      
      getOptimalIconProps: (customProps = {}) => ({
        className: modalSettings.iconSize,
        ...customProps
      }),
      
      getOptimalButtonProps: (customProps = {}) => ({
        className: `${modalSettings.buttonSize} ${customProps.className || ''}`,
        ...customProps
      })
    };
  }, []);
};

export default useModalOptimization;
